import {
  AfterViewInit,
  Component,
  computed,
  inject,
  ViewChild,
  type OnDestroy,
} from '@angular/core';
import {
  CommonModule,
  DATE_PIPE_DEFAULT_OPTIONS,
  DatePipe,
} from '@angular/common';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatChipsModule } from '@angular/material/chips';
import { provideNativeDateAdapter } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatDividerModule } from '@angular/material/divider';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatMenuModule } from '@angular/material/menu';
import { MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatSortModule, Sort } from '@angular/material/sort';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatTabsModule } from '@angular/material/tabs';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Router } from '@angular/router';
import debounce from 'lodash/debounce';

import {
  RequestsTableComponent,
  RequestTableAction,
  RequestTableConfig,
  RequestTableItem,
} from '../../shared/components/requests-table/requests-table.component';
import {
  FileSeedTableComponent,
  FileSeedTableAction,
  FileSeedTableConfig,
  FileSeedTableItem,
} from '../../shared/components/file-seed-table/file-seed-table.component';
import { EnhancedLoadingService } from '../../shared/services/enhanced-loading.service';
import { SmartOrpcClientService } from '../../shared/services/smart-orpc-client.service';
import { UserService } from '../../shared/services/user.service';
import { DatasafraSearchComponent } from '../../shared/components/datasafra-search/datasafra-search.component';
import {
  MonitoringFilterComponent,
  MonitoringFilterParams,
} from '../../shared/components/monitoring-filter/monitoring-filter.component';

import { isAbortOrAbortCause } from '../../shared/utils/is-abort-or-abort-cause';
import {
  MonitoringFilterModalComponent,
  MonitoringFilterModalParams,
} from '../../shared/components/monitoring-filter-modal/monitoring-filter-modal.component';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { UserRole } from '#backend/common/user-types';
import { createNestedOrder } from '../../shared/utils/create-nested-order';
import { downloadUrlWithAnchor } from '../../shared/utils/file-download';

export interface SearchParams {
  searchText?: string;
  range?: {
    start?: Date;
    end?: Date;
  };
  filterParams?: MonitoringFilterParams;
  onlyMine?: boolean;
}

interface MonitoringRequestTableItem extends RequestTableItem {
  totalAreaHa?: number | null;
  totalAreaCount?: number | null;
  organizationId?: number | null;
  organizationName?: string | null;
  userName?: string | null;
}

@Component({
  selector: 'app-monitoring-safras',
  standalone: true,
  imports: [
    CommonModule,
    DatasafraSearchComponent,
    DatePipe,
    FormsModule,
    MatButtonModule,
    MatCardModule,
    MatChipsModule,
    MatDatepickerModule,
    MatDividerModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatMenuModule,
    MatPaginatorModule,
    MatProgressBarModule,
    MatSnackBarModule,
    MatSortModule,
    MatTableModule,
    MatTabsModule,
    MatTooltipModule,
    MonitoringFilterComponent,
    ReactiveFormsModule,
    RequestsTableComponent,
    FileSeedTableComponent,
    MonitoringFilterModalComponent,
  ],
  templateUrl: './monitoring-safras.component.html',
  styleUrl: './monitoring-safras.component.scss',
  providers: [
    provideNativeDateAdapter(),
    {
      provide: DATE_PIPE_DEFAULT_OPTIONS,
      useValue: { dateFormat: 'short', timezone: '-03:00' },
    },
  ],
})
export class MonitoringSafrasComponent implements AfterViewInit, OnDestroy {
  userService = inject(UserService);
  smartClient = inject(SmartOrpcClientService);
  loadingService = inject(EnhancedLoadingService);
  private snackBar = inject(MatSnackBar);

  // Observables para diferentes tipos de loading
  globalLoading$ = this.loadingService.global$;
  gridLoading$ = this.loadingService.grid$;
  inputLoading$ = this.loadingService.input$;
  buttonLoading$ = this.loadingService.button$;
  isLoad$ = this.loadingService.isLoad$;

  // DataSources para cada aba
  dataSource = new MatTableDataSource<MonitoringRequestTableItem>([]);
  activeDataSource = new MatTableDataSource<MonitoringRequestTableItem>([]);
  fileSeedDataSource = new MatTableDataSource<FileSeedTableItem>([]);

  // Contadores para cada aba
  length = 0;
  activeLength = 0;
  fileSeedLength = 0;

  // Aba selecionada atualmente
  selectedTabIndex = 0;

  // Filtros aplicados
  currentFilters: MonitoringFilterParams = {};

  // Filtros do modal (para controle do botão Gerar Arquivo Semente)
  currentModalFilters: MonitoringFilterModalParams = {};

  // Estado de loading para uso no canGenerateSeedFile
  isAnyLoading = false;

  canCreateRequest = computed(() => {
    const user = this.userService.user();
    if (!user) return false;
    return [
      UserRole.ADMIN_CLIENTE,
      UserRole.OPERADOR_GEOAMBIENTE,
      UserRole.ADMIN_GEOAMBIENTE,
    ].includes(user.role);
  });

  // Computed property para verificar se o botão pode ser habilitado
  get canGenerateSeedFile(): boolean {
    // Apenas admins podem gerar arquivos semente
    if (!this.userService.isGeoambiente) {
      return false;
    }

    // O botão só deve ser habilitado na aba 0 (Solicitação)
    if (this.selectedTabIndex !== 0) {
      return false;
    }

    const filters = this.currentModalFilters;

    const allFieldsFilled = !!(
      // null = geoambiente, undefined = não preenchido
      filters.organizationId !== undefined &&
      filters.safra &&
      filters.cultura &&
      filters.status
    );

    const isValidStatus = filters.status === 'ANALYZING';

    return allFieldsFilled && isValidStatus && this.dataSource.data.length > 0;
  }

  get displayedColumns(): string[] {
    const columns = [
      'id',
      'cropPeriod',
      'cropCulture',
      ...(this.userService.isGeoambiente ? ['cliente'] : []),
      'createdBy',
      'createdDate',
      'status',
    ];

    if (this.userService.isGeoambiente) {
      columns.push('consistency');
    }

    return columns;
  }

  // Configuração da tabela
  get tableConfig(): RequestTableConfig {
    return {
      showExpandColumn: true,
      showActionsColumn: false,
      showViewAdminColumn: this.userService.isGeoambiente,
      columns: [...this.displayedColumns],
      statusClassFunction: this.getStatusClass,
      initialSort: { active: 'createdDate', direction: 'desc' },
    };
  }

  // Ações da tabela
  tableActions: RequestTableAction[] = [
    {
      label: 'Acessar resultados',
      icon: 'description',
      action: (item: RequestTableItem) => this.goToMonitoringReportsPage(item),
      condition: (item: RequestTableItem) => item.status.code === 'COMPLETED',
    },
  ];

  // Configuração da tabela de arquivos semente
  get fileSeedTableConfig(): FileSeedTableConfig {
    return {
      showActionsColumn: true,
      columns: [
        'id',
        'gcsFieldsShapeUriOutput',
        'userName',
        'createdDate',
        'statusSeed',
        'lastDownloadOutput',
        'statusFileSrc',
        'lastUploadInput',
        'consistency',
        'error',
        'errorDate',
      ],
      initialSort: { active: 'createdDate', direction: 'desc' },
    };
  }

  // Ações da tabela de arquivos semente
  fileSeedTableActions: FileSeedTableAction[] = [
    {
      label: 'Download',
      icon: 'download',
      action: (item, fileType) => this.downloadFileSeed(item, fileType),
      condition: () => this.userService.isGeoambiente,
    },
    {
      label: 'Upload',
      icon: 'upload',
      action: (item) => this.uploadFileSeed(item),
      condition: () => this.userService.isGeoambiente,
    },
  ];

  @ViewChild(DatasafraSearchComponent)
  declare search: DatasafraSearchComponent;

  @ViewChild(RequestsTableComponent, { read: RequestsTableComponent })
  declare monitoringRequestsTable: RequestsTableComponent;

  @ViewChild(FileSeedTableComponent, { read: FileSeedTableComponent })
  declare monitoringFileTable: FileSeedTableComponent;

  abortLoad: AbortController | undefined;

  // Armazena os últimos parâmetros de busca para manter estado na paginação
  private lastSearchParams: SearchParams = {};

  // Debounced functions para busca com loading apropriado
  debouncedSearchLoad = debounce(
    (v: SearchParams) => this.loadWithInputScope(v),
    500,
  );

  constructor(private readonly router: Router) {}

  // Métodos auxiliares para acessar ViewChild de forma segura
  private getRequestsTablePaginator() {
    if (!this.monitoringRequestsTable) {
      console.warn('RequestsTableComponent não está disponível');
    }
    return this.monitoringRequestsTable?.paginator;
  }

  private getFileSeedTablePaginator() {
    if (!this.monitoringFileTable) {
      console.warn('FileSeedTableComponent não está disponível');
    }
    return this.monitoringFileTable?.paginator;
  }

  private getCurrentPaginator() {
    return this.selectedTabIndex === 1
      ? this.getFileSeedTablePaginator()
      : this.getRequestsTablePaginator();
  }

  asTypedItem = (x: any) => x as MonitoringRequestTableItem;

  navigateToCreatePage(): void {
    this.router.navigate(['/monitoring-safras/new']);
  }

  // Métodos auxiliares para loading com escopo apropriado
  async loadWithInputScope(params: SearchParams): Promise<void> {
    this.loadingService.show('input');
    try {
      await this.handleLoadWithScope(params, 'Erro na busca:', undefined);
    } finally {
      this.loadingService.hide('input');
    }
  }

  async loadWithGridScope(
    params: SearchParams = {},
    statusFilter?: string[],
  ): Promise<void> {
    this.loadingService.show('grid');
    try {
      await this.handleLoadWithScope(
        params,
        'Erro no carregamento da grid:',
        statusFilter,
      );
    } finally {
      this.loadingService.hide('grid');
    }
  }

  // Método auxiliar centralizado para loading
  private async handleLoadWithScope(
    params: SearchParams,
    errorMsg: string,
    statusFilter?: string[],
  ): Promise<void> {
    this.abortLoad?.abort();
    this.abortLoad = new AbortController();

    try {
      console.log(this.monitoringRequestsTable);
      const paginator = this.getRequestsTablePaginator();
      const sort = this.getCurrentDataSource().sort;
      const [data, count] = await this.smartClient.client.monitoring.list(
        {
          cursor: (paginator?.pageIndex ?? 0) * (paginator?.pageSize ?? 20),
          limit: paginator?.pageSize ?? 20,
          textSearch: params.searchText ?? undefined,
          dateRange: {
            from: params.range?.start ?? undefined,
            to: params.range?.end ?? undefined,
          },
          onlyMine: params.onlyMine ?? undefined,
          orderBy:
            sort != null && sort.active ? createNestedOrder(sort) : undefined,

          organizationId:
            this.selectedTabIndex === 1
              ? params.filterParams?.organizationId
              : this.currentModalFilters.organizationId,
          status:
            statusFilter ??
            (this.currentModalFilters.status
              ? [this.currentModalFilters.status]
              : undefined),
          cultura: this.currentModalFilters.cultura
            ? this.currentModalFilters.cultura.id
            : undefined,
          safra: this.currentModalFilters.safra ?? undefined,
        },
        {
          signal: this.abortLoad.signal,
        },
      );

      const targetDataSource = this.getCurrentDataSource();
      targetDataSource.data = data.map((req) => {
        const tableItem: MonitoringRequestTableItem = {
          cultura: req.targetCulture?.description,
          cropCulture: req.targetCulture?.description,
          safra: req.cropYear!,
          cropPeriod: req.cropYear!,

          ...req,
        };

        return tableItem;
      }) satisfies MonitoringRequestTableItem[];

      if (this.selectedTabIndex === 0) {
        this.length = count;
      } else {
        this.activeLength = count;
      }
    } catch (error) {
      if (!isAbortOrAbortCause(error)) {
        console.error(errorMsg, error);
      }
    }
  }

  // Função de status class para as tabelas
  getStatusClass(statusCode: string): string {
    switch (statusCode) {
      case 'VALIDATING_INPUT':
        return 'chip-validando';
      case 'ANALYZING':
        return 'chip-validando';
      case 'COMPLETED':
        return 'chip-concluida';
      case 'FAILED':
        return 'chip-cancelada';
      default:
        return '';
    }
  }

  // Navegação para páginas de relatório
  goToMonitoringReportsPage(element: RequestTableItem): void {
    this.router.navigate(['monitoring-safras/reports'], {
      queryParams: { batchRequestId: element.id },
    });
  }

  onRowToggle(element: RequestTableItem): void {
    // Lógica para expansão de linha se necessário
  }

  handleViewAdmin(item: RequestTableItem): void {
    this.router.navigate(['/monitoring-safras/adm/detail', item.id]);
  }

  // Carregar dados específicos da aba selecionada
  loadTabData(): void {
    const searchParams: SearchParams = {
      ...this.lastSearchParams, // Preserva os parâmetros de busca
      filterParams: this.currentFilters,
    };

    switch (this.selectedTabIndex) {
      case 0: // Solicitação
        this.loadWithGridScope(searchParams);
        break;
      case 1: // Arquivos semente
        // Apenas carregar dados se o usuário for admin
        if (this.userService.isGeoambiente) {
          this.loadFileSeedData();
        }
        break;
    }
  }

  // Carregar dados dos arquivos semente
  async loadFileSeedData(): Promise<void> {
    this.abortLoad?.abort();
    this.abortLoad = new AbortController();

    try {
      const parts: string[] = [];
      if (this.currentFilters.organizationId) {
        parts.push(String(this.currentFilters.organizationId));
      }
      if (this.currentFilters.safra) {
        parts.push(this.currentFilters.safra);
      }
      if (this.currentFilters.cultura?.description) {
        parts.push(this.currentFilters.cultura.description);
      }
      const filtersString = parts.length > 0 ? parts.join('|') : undefined;

      const paginator = this.getFileSeedTablePaginator();
      const request = {
        textSearch: filtersString,
        orderBy: { createdDate: 'desc' as const },
        cursor: (paginator?.pageIndex ?? 0) * (paginator?.pageSize ?? 20),
        limit: paginator?.pageSize ?? 20,
      };

      const response = await this.smartClient.client.monitoring.fileSeed.list(
        request,
        { signal: this.abortLoad.signal },
      );

      // O endpoint retorna [results, total]
      const [fileSeedData, totalCount] = response;

      // Converter dados para o formato da tabela
      const tableData: FileSeedTableItem[] = fileSeedData.map((item: any) => ({
        id: item.id,
        version: item.version,
        createdBy: item.createdBy,
        createdDate:
          item.createdDate instanceof Date
            ? item.createdDate.toISOString()
            : item.createdDate,
        status: this.getStatusFromItem(item),

        error: item.error,
        errorDate: item.errorDate
          ? item.errorDate instanceof Date
            ? item.errorDate.toISOString()
            : item.errorDate
          : null,
        gcsInconsistencyCsvUri: item.gcsInconsistencyCsvUri,
        gcsMonitoringShapeUriOutput: item.gcsMonitoringShapeUriOutput,
        gcsFieldsShapeUriOutput: item.gcsFieldsShapeUriOutput,
        isProcessing: item.isProcessing || false,
        isCompleted: item.isCompleted || false,
        isProcessingInput: item.isProcessingInput || false,
        isCompletedInput: item.isCompletedInput || false,
        validationSuccess: item.validationSuccess,
        lastDownloadOutput: item.lastDownloadOutput,
        lastUploadInput: item.lastUploadInput,
        userName: item.createdByUser?.name || '',
      }));

      this.fileSeedDataSource.data = tableData;
      this.fileSeedLength = totalCount;
    } catch (error) {
      if (!isAbortOrAbortCause(error)) {
        console.error('Erro ao carregar dados de file seed:', error);
      }
    }
  }

  private getStatusFromItem(item: any): string {
    if (item.error) {
      return 'FAILED';
    }
    if (item.isProcessing) {
      return 'PROCESSING';
    }
    if (item.isCompleted) {
      return 'COMPLETED';
    }
    return 'PENDING';
  }

  // Obter o data source atual baseado na aba selecionada
  getCurrentDataSource() {
    switch (this.selectedTabIndex) {
      case 1:
        return this.activeDataSource;
      default:
        return this.dataSource;
    }
  }

  // Obter o length atual baseado na aba selecionada
  getCurrentLength(): number {
    switch (this.selectedTabIndex) {
      case 1:
        return this.activeLength;
      default:
        return this.length;
    }
  }

  ngAfterViewInit() {
    this.loadTabData();
    this.isLoad$.subscribe((isLoading) => {
      this.isAnyLoading = isLoading;
    });
  }

  ngOnDestroy() {
    this.abortLoad?.abort();
  }

  handleSearch(params: SearchParams) {
    this.lastSearchParams = params; // Armazena os parâmetros
    this.getRequestsTablePaginator()?.firstPage();
    this.debouncedSearchLoad(params);
  }

  handleFilterChange(filterParams: MonitoringFilterParams) {
    this.currentFilters = filterParams;

    this.getFileSeedTablePaginator()?.firstPage();
    this.loadTabData();
  }

  handleModalFilterChange(filterParams: MonitoringFilterModalParams) {
    this.currentModalFilters = filterParams;
    this.dataSource.data = [];
    this.applyModalFilters(filterParams);
  }

  private applyModalFilters(filterParams: MonitoringFilterModalParams) {
    this.getRequestsTablePaginator()?.firstPage();

    if (this.selectedTabIndex === 0) {
      this.loadWithGridScope(this.lastSearchParams);
    } else {
      this.loadTabData();
    }
  }

  onSortChange(_: Sort) {
    this.loadTabData();
  }

  onPageChange(_: PageEvent) {
    this.loadTabData();
  }

  // Métodos específicos para a tabela de arquivos semente
  onFileSeedSortChange(_: Sort) {
    this.loadFileSeedData();
  }

  onFileSeedPageChange(_: PageEvent) {
    this.loadFileSeedData();
  }

  // Métodos para manipulação de arquivos semente
  async downloadFileSeed(
    item: FileSeedTableItem,
    fileType: 'fields' | 'monitoring' | 'inconsistency',
  ): Promise<void> {
    try {
      this.loadingService.show('button');

      // Download do arquivo baseado no tipo
      const response =
        await this.smartClient.client.monitoring.fileSeed.download({
          id: item.id,
          fileType: fileType,
        });

      downloadUrlWithAnchor(response.downloadUrl);
    } catch (error) {
      console.error('Erro ao fazer download do arquivo semente:', error);
    } finally {
      this.loadingService.hide('button');
    }
  }

  async uploadFileSeed(item: FileSeedTableItem): Promise<void> {
    this.router.navigate(['/monitoring-safras/adm/upload-plot', item.id]);
  }

  // Método para lidar com a mudança de aba
  onTabChange(index: number): void {
    // Verificar se usuários não-admin estão tentando acessar aba de arquivos semente
    if (index === 1 && !this.userService.isGeoambiente) {
      // Manter na aba atual ou voltar para a primeira aba
      this.selectedTabIndex = 0;
      return;
    }

    this.selectedTabIndex = index;

    // Resetar filtros ao trocar de aba
    this.currentFilters = {};
    this.currentModalFilters = {};
    // A busca (checkbox "Minhas solicitações" e período de datas) é
    // desmontada/remontada com valores padrão ao trocar de aba. Limpa o
    // cache aqui; o app-datasafra-search recém-criado reemite seu estado
    // real (defaults nativos do FormControl) assim que é montado.
    this.lastSearchParams = {};

    this.loadTabData();
  }

  async generateSeedFile(): Promise<void> {
    try {
      this.loadingService.show('button');

      // Obter IDs dos requests selecionados baseado nos filtros atuais
      const selectedRequestIds = this.dataSource.data
        .filter((request) => {
          // Aplicar filtros para determinar quais requests incluir
          const matchesFilters = this.matchesCurrentFilters(request);
          return matchesFilters;
        })
        .map((request) => request.id)
        .filter((id): id is number => id !== undefined);

      if (selectedRequestIds.length === 0) {
        this.snackBar.open(
          'Nenhum request encontrado com os filtros atuais',
          'Fechar',
          {
            duration: 4000,
          },
        );
        return;
      }

      // Construir string de filtros baseada nos filtros do modal
      const filtersString = `${this.currentModalFilters.organizationId}|${this.currentModalFilters.safra}|${this.currentModalFilters.cultura?.description}`;

      // Chamar o endpoint para gerar arquivo semente
      const response =
        await this.smartClient.client.monitoring.fileSeed.generate({
          monitoringIds: selectedRequestIds,
          filters: filtersString,
        });

      if (response.success) {
        // Mostrar mensagem de processamento em background
        this.snackBar.open(
          'Processando arquivo semente em segundo plano. Você pode acompanhar o progresso na aba "Arquivos Semente".',
          'Fechar',
          {
            duration: 6000,
          },
        );

        // Trocar automaticamente para a aba de arquivos semente
        this.selectedTabIndex = 1;
        await this.loadFileSeedData();
      } else {
        this.snackBar.open(
          `Erro ao iniciar geração: ${response.message}`,
          'Fechar',
          {
            duration: 5000,
          },
        );
      }
    } catch {
      this.snackBar.open('Erro interno ao gerar arquivo semente', 'Fechar', {
        duration: 5000,
      });
    } finally {
      this.loadingService.hide('button');
    }
  }

  private matchesCurrentFilters(request: MonitoringRequestTableItem): boolean {
    const filters = this.currentModalFilters;

    if (
      filters.organizationId &&
      request.organizationId !== filters.organizationId
    ) {
      return false;
    }

    if (filters.safra && request.safra !== filters.safra) {
      return false;
    }

    if (filters.cultura && request.cultura !== filters.cultura.description) {
      return false;
    }

    if (filters.status && request.status?.code !== filters.status) {
      return false;
    }

    return true;
  }
}
